import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import {
  AccountingReportsService,
  AgedPayable,
  ReportDiagnostic,
  ReportResponse
} from '../../accounting-reports.service';
import { AgedReceivable } from '../aged-receivable/aged-receivable.model';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { ReportEmailComponent } from '../shared/report-email.component';
import { closeFinancialReportPdf, downloadFinancialReportPdf, openFinancialReportPdf } from '../shared/financial-report-pdf';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';

type AgingMode = 'receivable' | 'payable';
type AgingRow = AgedReceivable | AgedPayable;

interface AgingGroup {
  id: string;
  partnerId: number;
  partnerName: string;
  partnerType: string;
  currencyCode: string;
  bucketTotals: Record<string, number>;
  totalBalance: number;
  children: AgingRow[];
}

@Component({
  selector: 'app-aging-report',
  standalone: true,
  templateUrl: './aging-report.component.html',
  styleUrls: ['./aging-report.component.css'],
  imports: [
    CommonModule, FormsModule, ButtonModule, CardModule, DatePickerModule, IftaLabelModule,
    InputNumberModule, ProgressBarModule, SelectModule, TableModule, ToastModule,
    PageShellComponent, PageHeroComponent, PageFormSectionComponent, PageDataPanelComponent, ReportEmailComponent,
    LogisticsProjectLookupComponent
  ],
  providers: [AccountingReportsService, CurrencyPipe, MessageService, SessionStorageService]
})
export class AgingReportComponent implements OnInit {
  readonly reportActions = new PendingActionTracker<string>();
  mode: AgingMode = 'receivable';
  reportEndDate = new Date();
  intervalDays = 30;
  intervalCount = 4;
  agingBasis = 'DUE_DATE';
  agingBasisOptions = [
    { label: 'Due Date', value: 'DUE_DATE' },
    { label: 'Invoice / Bill Date', value: 'INVOICE_DATE' }
  ];
  rows: AgingRow[] = [];
  groups: AgingGroup[] = [];
  diagnostics: ReportDiagnostic[] = [];
  selectedLogisticsProjectId?: number | null;
  isLoading = false;
  private requestNumber = 0;

  constructor(
    route: ActivatedRoute,
    private reports: AccountingReportsService,
    private session: SessionStorageService,
    private messages: MessageService,
    private currency: CurrencyPipe
  ) {
    this.mode = route.snapshot.data['agingMode'] === 'payable' ? 'payable' : 'receivable';
  }

  ngOnInit(): void {
    this.load();
  }

  get title(): string { return this.mode === 'payable' ? 'Aged Payables' : 'Aged Receivables'; }
  get subtitle(): string { return this.mode === 'payable' ? 'Analyze open vendor bills by aging band.' : 'Analyze open customer invoices by aging band.'; }
  get partyLabel(): string { return this.mode === 'payable' ? 'Vendor' : 'Customer'; }
  get documentLabel(): string { return this.mode === 'payable' ? 'Bill Number' : 'Invoice Number'; }
  get totalLabel(): string { return this.mode === 'payable' ? 'Total Payables' : 'Total Receivables'; }

  get bucketLabels(): string[] {
    const labels = Array.from({ length: this.intervalCount }, (_, index) => {
      const lower = index * this.intervalDays + (index === 0 ? 0 : 1);
      return `${lower}-${(index + 1) * this.intervalDays} Days`;
    });
    labels.push(`Over ${this.intervalDays * this.intervalCount} Days`);
    return labels;
  }

  get currencies(): string[] {
    return [...new Set(this.groups.map(group => group.currencyCode))];
  }

  totalBalance(currencyCode: string): number {
    return this.groups.filter(group => group.currencyCode === currencyCode)
      .reduce((sum, group) => sum + group.totalBalance, 0);
  }

  bucketTotal(label: string, currencyCode: string): number {
    return this.groups.filter(group => group.currencyCode === currencyCode)
      .reduce((sum, group) => sum + (group.bucketTotals[label] || 0), 0);
  }

  get emailRows(): Record<string, unknown>[] {
    return this.groups.flatMap(group => group.children.map(row => ({
      [this.partyLabel]: group.partnerName, Currency: group.currencyCode,
      Document: row.entryNumber, Date: row.entryDate, Reference: row.reference,
      Outstanding: row.transactionAmount, Days: row.daysOutstanding, Band: row.agingBucket
    })));
  }

  load(): void {
    const requestNumber = ++this.requestNumber;
    if (!this.reportEndDate) {
      this.rows = [];
      this.groups = [];
      this.isLoading = false;
      return;
    }
    this.intervalDays = Math.min(365, Math.max(1, Number(this.intervalDays) || 30));
    this.intervalCount = Math.min(8, Math.max(2, Number(this.intervalCount) || 4));
    this.isLoading = true;
    const request = (this.mode === 'payable'
      ? this.reports.getAgedPayableV2(this.companyId(), this.reportEndDate.getTime(), this.selectedLogisticsProjectId, this.intervalDays, this.intervalCount, this.agingBasis)
      : this.reports.getAgedReceivableV2(this.companyId(), this.reportEndDate.getTime(), this.selectedLogisticsProjectId, this.intervalDays, this.intervalCount, this.agingBasis)
    ) as import('rxjs').Observable<ReportResponse<AgingRow>>;
    request.subscribe({
      next: response => {
        if (requestNumber !== this.requestNumber) return;
        this.rows = response.rows ?? [];
        this.groups = this.groupRows(this.rows);
        this.diagnostics = response.diagnostics ?? [];
        this.isLoading = false;
      },
      error: error => {
        if (requestNumber !== this.requestNumber) return;
        console.error(`Failed to load ${this.title}`, error);
        this.rows = [];
        this.groups = [];
        this.diagnostics = [];
        this.isLoading = false;
        this.messages.add({ severity: 'error', summary: 'Error', detail: `Failed to load ${this.title}.` });
      }
    });
  }

  exportExcel(): void {
    if (!this.rows.length) return;
    const data: (string | number)[][] = [
      [this.title], ['Aging basis', this.agingBasis === 'DUE_DATE' ? 'Due Date' : 'Document Date'],
      ['Interval days', this.intervalDays], ['Intervals', this.intervalCount], [],
      [this.partyLabel, 'Type', this.documentLabel, 'Aging Date', 'Reference', 'Outstanding', 'Days', 'Band', 'Currency']
    ];
    this.groups.forEach(group => group.children.forEach(row => data.push([
      group.partnerName, group.partnerType, row.entryNumber, row.entryDate ? new Date(row.entryDate).toLocaleDateString() : '',
      row.reference || '', Number(row.transactionAmount) || 0, row.daysOutstanding, row.agingBucket, row.currencyCode
    ])));
    const sheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = { Sheets: { Aging: sheet }, SheetNames: ['Aging'] };
    const blob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })]);
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${this.title.replace(/\s/g, '')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }

  print(download = false): void {
    const action = download ? 'download' : 'print';
    if (!this.reportActions.begin(action)) return;
    // Open the tab synchronously while it is still associated with the user's click.
    // The generated document is then loaded as a PDF rather than printing this SPA view.
    const target = download ? null : window.open('', '_blank');
    this.reports.printFinanceReport(this.mode === 'payable' ? 'aged-payable' : 'aged-receivable', this.companyId(), {
      endDate: this.reportEndDate,
      logisticsProjectId: this.selectedLogisticsProjectId,
      intervalDays: this.intervalDays,
      intervalCount: this.intervalCount,
      agingBasis: this.agingBasis
    }).pipe(finalize(() => this.reportActions.end(action))).subscribe({
      next: blob => download ? downloadFinancialReportPdf(blob, this.mode === 'payable' ? 'aged-payable' : 'aged-receivable') : openFinancialReportPdf(blob, target),
      error: error => {
        console.error(`Failed to generate ${this.title} PDF`, error);
        closeFinancialReportPdf(target);
        this.messages.add({
          severity: 'error',
          summary: 'Print unavailable',
          detail: `Failed to generate the ${this.title} PDF.`
        });
      }
    });
  }

  private groupRows(rows: AgingRow[]): AgingGroup[] {
    const groups = new Map<string, AgingGroup>();
    rows.forEach(row => {
      const partnerName = 'partnerName' in row ? row.partnerName : row.vendorName;
      const id = `${row.partnerType}:${row.partnerId}:${row.currencyCode}`;
      const group = groups.get(id) ?? {
        id, currencyCode: row.currencyCode,
        partnerId: row.partnerId, partnerName: partnerName || `Partner ${row.partnerId}`,
        partnerType: row.partnerType, bucketTotals: {}, totalBalance: 0, children: []
      };
      const amount = Number(row.transactionAmount) || 0;
      group.children.push(row);
      group.totalBalance += amount;
      group.bucketTotals[row.agingBucket] = (group.bucketTotals[row.agingBucket] || 0) + amount;
      groups.set(id, group);
    });
    return [...groups.values()].sort((a, b) => a.partnerName.localeCompare(b.partnerName));
  }

  private companyId(): number {
    return this.session.getCompany()?.id ?? 0;
  }
}
